import { createFileRoute } from '@tanstack/react-router';
import type { AppEmbed as AppEmbedType } from '@thoughtspot/visual-embed-sdk';
import { AppEmbed, useEmbedRef } from '@thoughtspot/visual-embed-sdk/react';
import { useUserAppViewConfig } from '@/api/queries/useSdkTokenQuery';

interface RouteParams {
  object_id: string;
}
interface RouteConfig {
  params: RouteParams;
}

interface ParsedParams {
  object_id: string;
}

/**
 * DEEP LINK ROUTE: Chart Viewer
 *
 * This route serves as a direct access point for sharing/bookmarking specific ThoughtSpot charts.
 * Users typically land here via:
 * - Email links containing chart URLs
 * - Shared links (Slack, Teams, etc.)
 * - Bookmarks
 * - External integrations
 *
 * URL Pattern: /dashboard/charts/{object_id}
 * Example: /dashboard/charts/5d6fc343-5d1b-4b2c-9d38-3378e1a20d3f
 *
 * This route uses a direct AppEmbed component with its own independent iframe,
 * not the prerendered shared iframe from PrerenderedThoughtSpotProvider.
 */
export const Route = createFileRoute('/dashboard/_deep-links/charts/$object_id')<RouteConfig>({
  component: RouteComponent,
  params: {
    parse: (params: Record<string, string>): ParsedParams => {
      const { object_id } = params;

      return {
        object_id,
      };
    },
  },
});

function RouteComponent() {
  const { object_id } = Route.useParams();
  const embedRef = useEmbedRef() as React.RefObject<AppEmbedType>;
  const userAppViewConfig = useUserAppViewConfig();

  return (
    <AppEmbed
      className="h-screen w-full"
      ref={embedRef}
      liveboardV2={true}
      enableAskSage={true}
      modularHomeExperience={true}
      dataPanelV2={true}
      // TODO: waiting for TS support verification whether the flag also needs to be explicitly passed on those deep-link routes
      // enableCustomColumnGroups={true}
      // additionalFlags={{ enableCustomColumnGroups: true }}
      path={`saved-answer/${object_id}`}
      {...(userAppViewConfig?.hiddenActions && {
        hiddenActions: userAppViewConfig.hiddenActions,
      })}
      {...(userAppViewConfig?.homePageSearchBarMode && {
        homePageSearchBarMode: userAppViewConfig.homePageSearchBarMode,
      })}
    ></AppEmbed>
  );
}
